import { useState } from "react";
import { Message, Media } from "@/types";
import { useLazyGetMessageMediaContentQuery } from "@/store/conversations/apis";
import { Download } from "lucide-react";

interface MessageAttachmentsProps {
  message: Message;
}

export const MessageAttachments = ({ message }: MessageAttachmentsProps) => {
  const [loadingMediaId, setLoadingMediaId] = useState<number | null>(null);

  const [getMessageMediaContent] = useLazyGetMessageMediaContentQuery();

  if (!message.media || message.media.length === 0) {
    return null;
  }

  const handleAttachmentClick = async (media: Media) => {
    try {
      setLoadingMediaId(media.id);
      const result = await getMessageMediaContent({
        messageId: message.id,
        mediaId: media.id,
      }).unwrap();

      if (result?.url) {
        // Fetching the file first instead of using anchor tag to
        // to force the filename to be the media.name instead of the url
        fetch(result.url)
          .then((response) => response.blob())
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = media.name || "attachment";
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            }, 100);
          })
          .catch((error) => {
            console.error("Error downloading file:", error);
          });
      }
    } catch (error) {
      console.error("Error fetching media content URL:", error);
    } finally {
      setLoadingMediaId(null);
    }
  };

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-500 mb-1">Attachments</div>
      <div className="flex flex-col gap-1">
        {message.media.map((media: Media) => {
          const mediaId = media.id;
          const fileName = media?.name || "Attachment";
          const isAttachmentLoading = loadingMediaId === mediaId;

          return (
            <button
              key={mediaId}
              onClick={() => handleAttachmentClick(media)}
              className="text-blue-500 hover:text-blue-700 text-sm flex items-center"
              disabled={isAttachmentLoading}
            >
              <Download size={16} className="mr-1" />
              {isAttachmentLoading ? "Loading..." : fileName}
            </button>
          );
        })}
      </div>
    </div>
  );
};
